import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ListOrdered, Copy, Check, ScanSearch } from 'lucide-react';
import { coreEvents } from '@/lib/useClaimDemo';

const btn = 'rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors disabled:opacity-40';
const time = (t) => { if (!t) return 'none'; const s = new Date(t * 1000).toISOString(); return `${s.slice(5, 7)}-${s.slice(8, 10)} ${s.slice(11, 16)} UTC`; };

function Actions({ demo }) {
  if (!demo.current.events.length) {
    return (
      <div className="flex flex-wrap gap-2" data-testid="demo-actions">
        <button type="button" onClick={demo.checkStatus} className={`${btn} border-white/15 text-white hover:border-white/40`} data-testid="demo-check-status-btn">Check status</button>
        <button type="button" onClick={demo.acknowledge} className={`${btn} border-sky-500/50 text-sky-200 hover:bg-sky-500/10`} data-testid="demo-acknowledge-btn">Merchant: add &quot;received&quot;</button>
      </div>
    );
  }
  const last = demo.current.events[demo.current.events.length - 1];
  const contested = last.status === 'contested';
  return (
    <div className="flex flex-wrap gap-2" data-testid="demo-actions">
      <button type="button" onClick={demo.checkStatus} className={`${btn} border-white/15 text-white hover:border-white/40`} data-testid="demo-check-status-btn">Check status</button>
      {contested ? (
        <>
          <button type="button" onClick={() => demo.decide('approved', 'contest upheld', 'original payment method')} className={`${btn} border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10`} data-testid="demo-contest-upheld-btn">Person: contest upheld</button>
          <button type="button" onClick={() => demo.decide('denied', 'contest denied')} className={`${btn} border-red-500/40 text-red-200 hover:bg-red-500/10`} data-testid="demo-contest-denied-btn">Person: contest denied</button>
        </>
      ) : (
        <>
          <button type="button" onClick={() => demo.decide('approved', 'approved in full', 'original payment method')} className={`${btn} border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10`} data-testid="demo-approve-btn">Staff: approve</button>
          <button type="button" onClick={() => demo.decide('denied', 'denied under policy')} className={`${btn} border-red-500/40 text-red-200 hover:bg-red-500/10`} data-testid="demo-deny-btn">Staff: deny</button>
        </>
      )}
      <button type="button" onClick={demo.contest} className={`${btn} border-amber-400/50 text-amber-200 hover:bg-white/5`} data-testid="demo-contest-btn">Customer: contest decision</button>
    </div>
  );
}

export function DemoTimeline({ demo }) {
  const [copied, setCopied] = useState(false);
  const c = demo.current;
  const copy = async () => {
    await navigator.clipboard.writeText(JSON.stringify(coreEvents(c.events), null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="demo-timeline-panel">
      <h2 className="text-base md:text-lg font-semibold mb-1 flex items-center gap-2"><ListOrdered className="w-4 h-4 text-amber-300" /> 3. Case timeline</h2>
      {!c ? <p className="text-sm text-gray-500" data-testid="demo-timeline-empty">Send a claim to open a case.</p> : (
        <>
          <p className="text-sm text-gray-400 mb-4">What the merchant holds. Every event is signed and linked to the one before it. Events are never edited or deleted.</p>
          {!c.events.length && <p className="text-sm text-sky-200/80 mb-4" data-testid="demo-timeline-no-events">The merchant has the claim but has not added a &quot;received&quot; event yet.</p>}
          <ol className="space-y-2 mb-5" data-testid="demo-timeline">
            {c.events.map((e) => (
              <li key={e.eventIndex} className="rounded-xl border border-white/10 bg-black/30 px-4 py-3" data-testid={`demo-event-${e.eventIndex}`}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <span className="font-mono text-xs text-gray-500">#{e.eventIndex}</span>
                  <span className="font-semibold text-white" data-testid={`demo-event-${e.eventIndex}-status`}>{e.status}</span>
                  {(e.missingInfo || e.decisionCode) && <span className="text-amber-200 text-xs">{e.missingInfo || e.decisionCode}</span>}
                  <span className="text-xs text-gray-500 ml-auto">next update by {time(e.nextUpdateBy)}</span>
                </div>
                <div className="font-mono text-[11px] text-gray-500 mt-1 break-all">hash {e.eventHash.slice(0, 18)}... prev {e.prevEventHash.slice(0, 10)}...</div>
              </li>
            ))}
          </ol>
          <Actions demo={demo} />
          <div className="flex flex-wrap gap-3 mt-5 pt-5 border-t border-white/5">
            <button type="button" disabled={!c.events.length} onClick={copy} className={`${btn} border-white/15 text-white hover:border-white/40 inline-flex items-center gap-1.5`} data-testid="demo-copy-timeline-btn">
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied' : 'Copy timeline JSON'}
            </button>
            <Link to="/specs/claim-records/checker" className={`${btn} border-amber-400/50 text-amber-200 hover:bg-white/5 inline-flex items-center gap-1.5`} data-testid="demo-open-checker-link"><ScanSearch className="w-3.5 h-3.5" /> Paste it into the checker</Link>
          </div>
        </>
      )}
    </section>
  );
}
